import { Button } from '@neo/index'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@neo/primitives/dialog'
import { Input } from '@neo/primitives/input'
import { Label } from '@neo/primitives/label'
import { Textarea } from '@neo/primitives/textarea'
import { useState } from 'react'

export interface TicketFields {
  subject: string
  description: string
  ccEmails: string
}

interface TicketDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialDescription?: string
  isSubmitting: boolean
  onSubmit: (fields: TicketFields) => void
}

const MAX_SUBJECT = 120
const MAX_DESCRIPTION = 2000

// Inner form lives inside DialogContent so it remounts (and resets state) each
// time the dialog opens — seeding the description from the `/ticket …` command.
const TicketFormFields = ({
  initialDescription = '',
  isSubmitting,
  onSubmit,
  onCancel,
}: {
  initialDescription?: string
  isSubmitting: boolean
  onSubmit: (fields: TicketFields) => void
  onCancel: () => void
}) => {
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState(initialDescription)
  const [ccEmails, setCcEmails] = useState('')
  const canSubmit = description.trim().length > 0 && !isSubmitting

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor="ticket-subject" className="text-s-400">
            Subject <span className="text-tertiary italic">(optional)</span>
          </Label>
          <Input
            id="ticket-subject"
            value={subject}
            maxLength={MAX_SUBJECT}
            placeholder="Brief summary of the issue"
            onChange={(e) => setSubject(e.target.value)}
            disabled={isSubmitting}
            data-testid="assistant-ticket-subject-input"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="ticket-description" className="text-s-400">
            Description
          </Label>
          <Textarea
            id="ticket-description"
            value={description}
            maxLength={MAX_DESCRIPTION}
            rows={5}
            placeholder="Describe what's going wrong, with any relevant ids or steps…"
            onChange={(e) => setDescription(e.target.value)}
            disabled={isSubmitting}
            data-testid="assistant-ticket-description-input"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="ticket-cc" className="text-s-400">
            CC teammates <span className="text-tertiary italic">(optional)</span>
          </Label>
          <Input
            id="ticket-cc"
            value={ccEmails}
            placeholder="teammate@yourcompany.com, lead@yourcompany.com"
            onChange={(e) => setCcEmails(e.target.value)}
            disabled={isSubmitting}
            data-testid="assistant-ticket-cc-input"
          />
          <span className="text-xxs-400 text-muted-foreground">
            Comma-separated. Only teammates on your company's email domain can be CC'd.
          </span>
        </div>
      </div>
      <DialogFooter>
        <Button
          size="sm"
          variant="ghost"
          onClick={onCancel}
          disabled={isSubmitting}
          data-testid="assistant-ticket-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!canSubmit}
          onClick={() => onSubmit({ subject, description, ccEmails })}
          data-testid="assistant-ticket-submit-btn"
        >
          {isSubmitting ? 'Raising…' : 'Raise ticket'}
        </Button>
      </DialogFooter>
    </>
  )
}

// Popup shown when a flagged user runs Buddy's `/ticket` command. Collects the
// issue details and hands them to the panel, which performs the network call and
// surfaces success/error. Bypasses the AI entirely.
export const TicketDialog = ({
  open,
  onOpenChange,
  initialDescription,
  isSubmitting,
  onSubmit,
}: TicketDialogProps) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-[460px]" data-testid="assistant-ticket-dialog">
      <DialogHeader>
        <DialogTitle>Raise a support ticket</DialogTitle>
        <DialogDescription>
          This goes straight to our support team — no AI in between.
        </DialogDescription>
      </DialogHeader>
      {open && (
        <TicketFormFields
          initialDescription={initialDescription}
          isSubmitting={isSubmitting}
          onSubmit={onSubmit}
          onCancel={() => onOpenChange(false)}
        />
      )}
    </DialogContent>
  </Dialog>
)
